import { Activity, Calendar, Cpu, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { trpc } from '../../lib/trpc';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';

interface TokenUsageCardProps {
  isAdmin: boolean;
}

function formatNumber(num: number): string {
  return new Intl.NumberFormat().format(num);
}

function formatMonthLabel(monthStr: string): string {
  if (!/^\d{4}-\d{2}$/.test(monthStr)) return monthStr;
  const [yyyy, mm] = monthStr.split('-');
  const date = new Date(Date.UTC(Number(yyyy), Number(mm) - 1, 1));
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/**
 * Raw monthly token breakdown -- BYOK workspaces only. A hosted-key workspace's usage is already
 * represented by `AiCreditsPanel`'s credit balance, which is the number that actually gates
 * them; showing token counts and a dollar estimate alongside it re-exposes the token math credits
 * exist to hide (see the ai-credit-system change). A BYOK workspace has no credit balance to show
 * -- it pays its own provider directly -- so token counts here are the one number it can use to
 * cross-check against its own Anthropic/OpenAI/Google invoice, and no dollar estimate is shown:
 * we don't know their provider's actual rate, only ours, and quoting ours would be misleading.
 */
export function TokenUsageCard({ isAdmin }: TokenUsageCardProps) {
  const [selectedMonth, setSelectedMonth] = useState<string | undefined>(undefined);

  const { data: keys } = trpc.aiProviderKeys.list.useQuery(undefined, { enabled: isAdmin });
  const hasActiveByok = keys?.some((key) => key.isActive) ?? false;

  const { data: usage, isLoading } = trpc.billing.getWorkspaceTokenUsage.useQuery(
    selectedMonth ? { month: selectedMonth } : undefined,
    {
      enabled: isAdmin && hasActiveByok,
      staleTime: 30_000,
    }
  );

  if (!isAdmin || !hasActiveByok) return null;

  const currentMonth = usage?.month ?? selectedMonth ?? '';

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden mt-6">
      <CardHeader className="border-b border-border/50 bg-muted/30">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Cpu className="h-5 w-5 text-primary animate-pulse" />
            <CardTitle>AI Token Usage & Monthly Breakdown</CardTitle>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 bg-background/80 border border-border/50 rounded-lg px-2.5 py-1 text-xs">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              <select
                value={currentMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="bg-transparent font-medium text-foreground focus:outline-none cursor-pointer"
              >
                {(usage?.availableMonths ?? [currentMonth]).map((m) => (
                  <option key={m} value={m}>
                    {formatMonthLabel(m)}
                  </option>
                ))}
              </select>
            </div>

            <Badge
              variant="outline"
              className="bg-primary/10 text-primary border-primary/20 gap-1 font-mono shrink-0"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {currentMonth}
            </Badge>
          </div>
        </div>
        <CardDescription>
          Monthly AI consumption meter and token breakdown for this workspace's BYOK usage --
          cross-check against your own provider's invoice.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        {isLoading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-muted/50 rounded-lg w-1/3" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="h-20 bg-muted/30 rounded-lg" />
              <div className="h-20 bg-muted/30 rounded-lg" />
            </div>
          </div>
        ) : (
          <>
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="p-4 rounded-xl border border-border/40 bg-background/50 space-y-1">
                <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  <span>Total Tokens</span>
                  <Activity className="h-4 w-4 text-primary" />
                </div>
                <div className="text-2xl font-extrabold tracking-tight">
                  {formatNumber(usage?.totalTokens ?? 0)}
                </div>
                <p className="text-xs text-muted-foreground">Combined input & output</p>
              </div>

              <div className="p-4 rounded-xl border border-border/40 bg-background/50 space-y-1">
                <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  <span>AI Actions Executed</span>
                  <Sparkles className="h-4 w-4 text-amber-500" />
                </div>
                <div className="text-2xl font-extrabold tracking-tight">
                  {formatNumber(usage?.actionsCount ?? 0)}
                </div>
                <p className="text-xs text-muted-foreground">Chat turns & email summaries</p>
              </div>
            </div>

            <div className="border-t border-border/40 pt-4 grid grid-cols-2 gap-4 text-sm text-muted-foreground">
              <div>
                <span className="font-medium text-foreground">Prompt Tokens (Input):</span>{' '}
                {formatNumber(usage?.promptTokens ?? 0)}
              </div>
              <div>
                <span className="font-medium text-foreground">Completion Tokens (Output):</span>{' '}
                {formatNumber(usage?.completionTokens ?? 0)}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
